import { useState } from 'react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Plus, Search } from 'lucide-react';
import { useCartoes } from '@/features/financeiro/hooks/useCadastrosFinanceiros';
import { CartoesTable } from '@/features/financeiro/components/CartoesTable';
import { CartaoDialog } from '@/features/financeiro/components/CartaoDialog';
import type { Cartao, SalvarCartaoRequest } from '@/features/financeiro/types';
import { usePaginacaoLocal } from '@/hooks/usePaginacaoLocal';
import { PaginacaoRodape } from '@/components/PaginacaoRodape';

const Cartoes = () => {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingCartao, setEditingCartao] = useState<Cartao | null>(null);
  const [busca, setBusca] = useState('');

  const { response, isLoading, criar, atualizar, inativar, isCriando, isAtualizando } = useCartoes(busca);
  const cartoes = response?.itens ?? [];
  const paginacao = usePaginacaoLocal(cartoes);

  const handleOpenDialog = (cartao?: Cartao) => {
    setEditingCartao(cartao ?? null);
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingCartao(null);
  };

  const handleSubmit = (data: SalvarCartaoRequest) => {
    if (editingCartao) {
      atualizar({ id: editingCartao.id, req: data }, { onSuccess: handleCloseDialog });
    } else {
      criar(data, { onSuccess: handleCloseDialog });
    }
  };

  if (isLoading) {
    return <div className="flex items-center justify-center h-96"><p className="text-muted-foreground">Carregando...</p></div>;
  }

  return (
    <div className="p-6 space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold">Cartões</h1>
          <p className="text-muted-foreground">Gerencie as bandeiras e operadoras de cartão</p>
        </div>
        <Button onClick={() => handleOpenDialog()}>
          <Plus className="h-4 w-4 mr-2" />Novo Cartão
        </Button>
      </div>

      <div className="relative max-w-sm">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input
          placeholder="Buscar cartão..."
          value={busca}
          onChange={(e) => setBusca(e.target.value)}
          className="pl-10"
        />
      </div>

      <CartoesTable
        cartoes={paginacao.itensPaginados}
        onEdit={handleOpenDialog}
        onInativar={(id) => inativar(id)}
      />

      <PaginacaoRodape
        pagina={paginacao.pagina}
        totalPaginas={paginacao.totalPaginas}
        totalItens={paginacao.totalItens}
        primeiroItem={paginacao.primeiroItem}
        ultimoItem={paginacao.ultimoItem}
        tamanhoPagina={paginacao.tamanhoPagina}
        onPaginaChange={paginacao.setPagina}
        onTamanhoPaginaChange={paginacao.setTamanhoPagina}
      />

      <CartaoDialog
        open={dialogOpen}
        onOpenChange={handleCloseDialog}
        onSubmit={handleSubmit}
        editingCartao={editingCartao}
        isLoading={isCriando || isAtualizando}
      />
    </div>
  );
};

export default Cartoes;
